import { useEffect, useRef, type ReactNode } from "react";

/** A blocking confirmation for actions that change what customers see.
 *
 * Focus moves into the dialog and Escape cancels, so the keyboard path is the
 * same as the pointer path. The confirm button is never the default focus for a
 * destructive action — an administrator pressing Enter out of habit should not
 * archive a product.
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  destructive,
  busy,
  confirmDisabled,
  onConfirm,
  onCancel,
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  busy?: boolean;
  /** Blocks confirmation while something the dialog itself asks for is still
   *  missing — the reason an order transition requires, for instance. Separate
   *  from `busy`, which means a request is already in flight. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus moves into the dialog ONCE, when it opens.
  //
  // This used to share an effect with the Escape listener below, whose
  // dependency is a callback most callers rebuild on every render — so the
  // focus call re-ran on every keystroke. That was invisible while every
  // confirmation was a title and a paragraph, and became a bug the moment one
  // of them contained a text field: the second character went to the Cancel
  // button instead of the input, and a space on a focused button dismisses it.
  //
  // Nothing is focused if the dialog's own content already claimed it — a body
  // with an `autoFocus` field is asking for focus deliberately, and the point
  // of this effect is to get focus INTO the dialog, not to a specific control.
  useEffect(() => {
    if (dialogRef.current?.contains(document.activeElement)) return;
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onCancel();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="dialog-backdrop" role="presentation">
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        ref={dialogRef}
      >
        <h2 className="dialog__title" id="confirm-title">
          {title}
        </h2>
        <div className="dialog__body">{children}</div>
        <div className="dialog__actions">
          <button
            type="button"
            className="btn"
            ref={cancelRef}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={destructive ? "btn btn--danger" : "btn btn--primary"}
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
